import { useEffect, useState } from 'react'
import { getOutlets, addPromoterSales } from '../../api/campaign';

export default function Promoter() {
    const [searchQuery, setSearchQuery] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [shopName, setShopName] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Promoters pick one of two fixed teams. Team membership is NOT the
    // supervisor name on the outlet row -- one supervisor (Niroshan) has shops
    // on both teams -- so the client's Team 01 / Team 02 split is mapped here
    // explicitly by shopId.
    const TEAMS = ['Team 01', 'Team 02'];
    const TEAM_OUTLETS = {
        'Team 01': [1, 2, 6, 7, 8, 9, 11, 12],
        'Team 02': [3, 4, 5, 10, 13, 14, 15],
    };

    const [outletNames, setOutletNames] = useState([]);
    const [outletIds, setOutletIds] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const rows = await getOutlets();
                const ids = {};
                rows.forEach(o => { ids[o.shopName] = o.shopId; });
                setOutletNames(rows.map(o => o.shopName));
                setOutletIds(ids);
            } catch (err) {
                console.log('error loading outlets: ', err);
            }
        })();
    }, []);

    // Weighted prices
    const prices = {
        atlas: {
            exercise: 122.00,
            cr: 292.00,
            b5: 207.00,
            others: 204.00
        },
        innovate: {
            exercise: 175.00,
            cr: 390.00,
            b5: 300.00
        }
    };

    // Shared header fields -- entered once, apply to both brand entries.
    const [teamName, setTeamName] = useState("");
    const [billingDate, setBillingDate] = useState("");
    const [timeSlot, setTimeSlot] = useState("f_half");

    // Per-brand sales figures. Both brands are filled on one screen and sent
    // together; a single-brand submit is blocked below.
    const [atlas, setAtlas] = useState({ exercise: "", b5: "", cr: "", others: "", billCount: "" });
    const [innovate, setInnovate] = useState({ exercise: "", b5: "", cr: "", billCount: "" });

    const colors = {
        bgFrom: '#1A0D2E',
        bgVia: '#5D1A5E',
        bgTo: '#B81F7B',
        primary: '#EC4899',
        primaryLight: '#F472B6',
        buttonFrom: '#EC4899',
        buttonTo: '#F472B6',
        textAccent: '#F9A8D4',
        textLight: '#FBCFE8',
        glow: 'rgba(236, 72, 153, 0.2)'
    };

    // Auto-calculated amount per brand.
    const atlasAmount = (
        (parseFloat(atlas.exercise) || 0) * prices.atlas.exercise +
        (parseFloat(atlas.b5) || 0) * prices.atlas.b5 +
        (parseFloat(atlas.cr) || 0) * prices.atlas.cr +
        (parseFloat(atlas.others) || 0) * prices.atlas.others
    );
    const innovateAmount = (
        (parseFloat(innovate.exercise) || 0) * prices.innovate.exercise +
        (parseFloat(innovate.b5) || 0) * prices.innovate.b5 +
        (parseFloat(innovate.cr) || 0) * prices.innovate.cr
    );

    // A brand counts as "entered" once any of its boxes has a value. Blanks are
    // saved as 0, but at least one figure must be present for each brand.
    const hasData = (d) => ['exercise', 'b5', 'cr', 'others', 'billCount']
        .some(k => d[k] !== "" && d[k] !== undefined);

    // Outlets available to the picked team, then narrowed by the search box.
    const teamShopIds = TEAM_OUTLETS[teamName] || [];
    const filteredOutlets = outletNames.filter(outlet =>
        teamShopIds.includes(outletIds[outlet]) &&
        outlet.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleShopSelect = (outlet) => {
        setShopName(outlet);
        setSearchQuery(outlet);
        setShowSuggestions(false);
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setShowSuggestions(true);
        setShopName("");
    };

    const handleTeamSelect = (team) => {
        setTeamName(team);
        // Switching team invalidates the current outlet pick.
        setShopName("");
        setSearchQuery("");
        setShowSuggestions(false);
    };

    const buildPayload = (game, d, amount) => ({
        teamName,
        shopName,
        shopId: outletIds[shopName] || null,
        game,
        billingDate,
        timeSlot,
        exercise: parseInt(d.exercise) || 0,
        b5: parseInt(d.b5) || 0,
        cr: parseInt(d.cr) || 0,
        others: game === 'atlas' ? (parseInt(d.others) || 0) : 0,
        billCount: parseInt(d.billCount) || 0,
        amount: parseFloat(amount.toFixed(2))
    });

    const handleSubmit = async () => {
        if (!teamName || !shopName || !billingDate || !timeSlot) {
            alert("Please fill all required fields");
            return;
        }
        if (!hasData(atlas) || !hasData(innovate)) {
            alert("Please enter sales for both Atlas and Innovate before submitting.");
            return;
        }

        setIsLoading(true);

        try {
            // Both brands go together -- if either write fails the promoter is
            // told, so a half-saved entry can't be mistaken for a full one.
            await addPromoterSales(buildPayload('atlas', atlas, atlasAmount));
            await addPromoterSales(buildPayload('innovate', innovate, innovateAmount));
            alert("Sales data submitted successfully!");

            // Reset form
            setTeamName("");
            setSearchQuery("");
            setShopName("");
            setBillingDate("");
            setTimeSlot("f_half");
            setAtlas({ exercise: "", b5: "", cr: "", others: "", billCount: "" });
            setInnovate({ exercise: "", b5: "", cr: "", billCount: "" });
        } catch (error) {
            console.log("Error submitting sales:", error);
            alert("Error submitting data. Please try again.");
        }

        setIsLoading(false);
    };

    const isFormValid = teamName && shopName && billingDate && timeSlot &&
        hasData(atlas) && hasData(innovate);

    const fieldClass = "bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white text-base rounded-xl focus:ring-2 focus:border-white block w-full px-3 py-2 shadow-lg placeholder:text-white/60";

    return (
        <div
            className='flex justify-center w-full min-h-screen relative overflow-hidden'
            style={{ background: `linear-gradient(to bottom right, ${colors.bgFrom}, ${colors.bgVia}, ${colors.bgTo})` }}
        >
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-32 h-32 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: colors.glow }}></div>
                <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: colors.glow, animationDelay: '1s' }}></div>
                <div className="absolute top-1/2 left-1/3 w-36 h-36 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: colors.glow, animationDelay: '2s' }}></div>
            </div>

            <div className="relative z-10 w-full px-6 py-8 flex flex-col justify-center max-w-md">
                <div className="text-center mb-8 animate-fade-in">
                    <div className="inline-block bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl mb-4 shadow-lg">
                        <h1 className='text-3xl uppercase font-bold text-white tracking-wider'>Sales Tracking</h1>
                    </div>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-white/20 animate-slide-up" style={{ overflow: 'visible' }}>
                    <form className="space-y-5" style={{ overflow: 'visible' }}>
                        {/* Team Name -- fixed two-team selection, no search */}
                        <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                            <label className="block text-lg font-semibold text-white drop-shadow-lg">Team Name</label>
                            <div className="flex gap-3">
                                {TEAMS.map((team) => (
                                    <button
                                        key={team}
                                        type="button"
                                        onClick={() => handleTeamSelect(team)}
                                        className={`flex-1 py-3 px-4 rounded-2xl font-bold text-base transition-all duration-300 transform hover:scale-105 active:scale-95 ${teamName === team ? 'bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-lg' : 'bg-white/20 text-white border-2 border-white/30'}`}
                                    >
                                        {team}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Outlet Name -- filtered to the selected team's outlets */}
                        <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.15s', position: 'relative', zIndex: 100 }}>
                            <label className="block text-lg font-semibold text-white drop-shadow-lg">Outlet Name</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    onFocus={() => {
                                        if (teamName) {
                                            setShowSuggestions(true);
                                        }
                                    }}
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    disabled={!teamName}
                                    className="bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white text-base rounded-2xl focus:ring-4 focus:border-white block w-full px-4 py-3 shadow-lg placeholder:text-white/60 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    placeholder={teamName ? 'Select Outlet Name' : 'Select a team first'}
                                />
                                {showSuggestions && teamName && (
                                    <div style={{ position: 'absolute', width: '100%', marginTop: '8px', zIndex: 9999 }}>
                                        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-white/30 max-h-60 overflow-y-auto animate-fade-in">
                                            {filteredOutlets.length > 0 ? (
                                                filteredOutlets.map((outlet, index) => (
                                                    <div
                                                        key={index}
                                                        onClick={() => handleShopSelect(outlet)}
                                                        className="px-5 py-3 hover:bg-pink-100 cursor-pointer transition-colors duration-200 text-gray-800 font-medium border-b border-gray-100 last:border-b-0"
                                                    >
                                                        {outlet}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-5 py-3 text-gray-500 text-center">
                                                    No shops found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Billing Date -- moved to the top, above the sales blocks */}
                        <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
                            <label className="block text-lg font-semibold text-white drop-shadow-lg">Billing Date</label>
                            <input
                                type='date'
                                value={billingDate}
                                onChange={(e) => setBillingDate(e.target.value)}
                                className="bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white text-base rounded-2xl focus:ring-4 focus:border-white block w-full px-4 py-3 shadow-lg transition-all duration-300"
                            />
                        </div>

                        {/* Time Slot -- moved to the top, above the sales blocks */}
                        <div className="space-y-2 animate-slide-up" style={{ animationDelay: '0.25s' }}>
                            <label className="block text-lg font-semibold text-white drop-shadow-lg">Time Slot</label>
                            <select
                                value={timeSlot}
                                onChange={(e) => setTimeSlot(e.target.value)}
                                className="bg-white/20 backdrop-blur-sm border-2 border-white/30 text-white text-base rounded-2xl focus:ring-4 focus:border-white block w-full px-4 py-3 shadow-lg transition-all duration-300"
                            >
                                <option value="f_half" className="bg-gray-800">1st Half</option>
                                <option value="s_half" className="bg-gray-800">2nd Half</option>
                                <option value="t_half" className="bg-gray-800">3rd Half</option>
                            </select>
                        </div>

                        {/* Atlas Sales -- shown together with Innovate, submitted at once */}
                        <div className="space-y-4 animate-slide-up pt-4 border-t border-white/20" style={{ animationDelay: '0.3s' }}>
                            <h3 className="text-xl font-bold text-white text-center">Atlas Sales</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">Exercise</label>
                                    <input type='number' value={atlas.exercise} onChange={(e) => setAtlas({ ...atlas, exercise: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">B5</label>
                                    <input type='number' value={atlas.b5} onChange={(e) => setAtlas({ ...atlas, b5: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">CR</label>
                                    <input type='number' value={atlas.cr} onChange={(e) => setAtlas({ ...atlas, cr: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">Others</label>
                                    <input type='number' value={atlas.others} onChange={(e) => setAtlas({ ...atlas, others: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">Bill Count</label>
                                    <input type='number' value={atlas.billCount} onChange={(e) => setAtlas({ ...atlas, billCount: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">Amount (Auto)</label>
                                    <input type='text' value={atlasAmount ? atlasAmount.toFixed(2) : ""} readOnly className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white text-base rounded-xl block w-full px-3 py-2 shadow-lg cursor-not-allowed" placeholder="0.00" />
                                </div>
                            </div>
                        </div>

                        {/* Innovate Sales */}
                        <div className="space-y-4 animate-slide-up pt-4 border-t border-white/20" style={{ animationDelay: '0.35s' }}>
                            <h3 className="text-xl font-bold text-white text-center">Innovate Sales</h3>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">Exercise</label>
                                    <input type='number' value={innovate.exercise} onChange={(e) => setInnovate({ ...innovate, exercise: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">B5</label>
                                    <input type='number' value={innovate.b5} onChange={(e) => setInnovate({ ...innovate, b5: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">CR</label>
                                    <input type='number' value={innovate.cr} onChange={(e) => setInnovate({ ...innovate, cr: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-white/90">Bill Count</label>
                                    <input type='number' value={innovate.billCount} onChange={(e) => setInnovate({ ...innovate, billCount: e.target.value })} className={fieldClass} placeholder="0" />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <label className="block text-sm font-semibold text-white/90">Amount (Auto)</label>
                                    <input type='text' value={innovateAmount ? innovateAmount.toFixed(2) : ""} readOnly className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white text-base rounded-xl block w-full px-3 py-2 shadow-lg cursor-not-allowed" placeholder="0.00" />
                                </div>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className='pt-6 animate-slide-up' style={{ animationDelay: '0.4s' }}>
                            <button
                                onClick={handleSubmit}
                                type="button"
                                disabled={isLoading || !isFormValid}
                                className="relative w-full text-white font-bold text-lg rounded-2xl px-8 py-5 shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:transform-none disabled:cursor-not-allowed overflow-hidden group"
                                style={{
                                    background: (isLoading || !isFormValid) ? 'linear-gradient(to right, #9CA3AF, #6B7280)' : `linear-gradient(to right, ${colors.buttonFrom}, ${colors.buttonTo})`,
                                    boxShadow: `0 20px 50px ${colors.glow}`
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                                {isLoading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Submitting...</span>
                                    </div>
                                ) : (
                                    <span className="relative z-10">Submit Sales Data</span>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style jsx>{`
                @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slide-up { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                .animate-fade-in { animation: fade-in 0.8s ease-out forwards; }
                .animate-slide-up { animation: slide-up 0.6s ease-out forwards; }
            `}</style>
        </div>
    )
}
